import React, { useRef, useState } from 'react';

import EnhancedTableRow, { defaultCellSx, EnhancedTableCell } from '../EnhancedTableRow';
import { EMPTY_ROW, KeyphraseRow } from '../../../utils/keyphrases';
import {
	Alert,
	Autocomplete,
	Button,
	Checkbox,
	createFilterOptions,
	IconButton,
	Popover,
	SxProps,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import MultilineTooltip from '../../MultilineTooltip';
import AlertDialog from '../../AlertDialog';

interface Props {
	rowIdx: number;
	row: KeyphraseRow;
	updateRow: (row: KeyphraseRow) => void;
	tagOptions: string[];
	updateTagOptions: (opts: string[]) => void;
	disableDelete: boolean;
	onDelete: (rowsToRemove?: KeyphraseRow[]) => void;
	onAdd: (newRow?: KeyphraseRow[]) => void;
	changeInProgress?: boolean;
	bottomRow?: EnhancedTableCell;
}

const KeyphraseTableRow: React.FC<Props> = ({
	rowIdx,
	row,
	updateRow,
	tagOptions,
	updateTagOptions,
	disableDelete,
	onDelete,
	onAdd,
	changeInProgress,
	bottomRow,
}) => {
	const theme = useTheme();
	const rowRef = useRef<null | HTMLTableRowElement>(null);
	const tagFilter = createFilterOptions<typeof row.tag>();

	const [excessiveLines, setExcessiveLines] = useState<number | null>(null);
	const [excessiveLength, setExcessiveLength] = useState(false);
	const [rowsToAdd, setRowsToAdd] = useState<KeyphraseRow[]>([]);
	const [nothingToPasteError, setNothingToPasteError] = useState(false);

	const textChangeHandler = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: keyof KeyphraseRow) => {
		updateRow({
			...row,
			[field]: e.target.value,
		});
	};

	const valChangeHandler = (val: boolean | string | null, field: keyof KeyphraseRow) => {
		updateRow({
			...row,
			[field]: val,
		});
	};

	const getErrorSx = (field: keyof KeyphraseRow): SxProps => {
		if (row.errorFields && row.errorFields[field]) {
			return {
				...defaultCellSx,
				border: 1,
				borderColor: theme.palette.error.main,
			};
		} else {
			return defaultCellSx;
		}
	};

	const keyHandler = (cellIndex: number, direction: 'UP' | 'DOWN' | 'LEFT' | 'RIGHT') => {
		let cellParent: Element | null = null;
		const cellRoot = 'enhanced-table-keyphrase-row-';

		if (direction === 'UP') {
			cellParent = rowRef.current?.previousElementSibling?.children.namedItem(
				`${cellRoot}${rowIdx - 1}-column-${cellIndex}`
			) as Element | null;
		} else if (direction === 'DOWN') {
			cellParent = rowRef.current?.nextElementSibling?.children.namedItem(`${cellRoot}${rowIdx + 1}-column-${cellIndex}`) as Element | null;
		} else if (direction === 'LEFT' && cellIndex > 0) {
			cellParent = rowRef.current?.children.namedItem(`${cellRoot}${rowIdx}-column-${cellIndex - 1}`) as Element | null;
		} else if (direction === 'RIGHT' && cellIndex < tableCells.length - 1) {
			cellParent = rowRef.current?.children.namedItem(`${cellRoot}${rowIdx}-column-${cellIndex + 1}`) as Element | null;
		}

		const cellOpts = cellParent?.querySelectorAll('input') as NodeListOf<HTMLInputElement> | null;
		if (cellOpts && cellOpts.length > 0) {
			cellOpts[0].focus();
		}
	};

	const pasteHandler = (event: React.ClipboardEvent<HTMLDivElement>) => {
		setRowsToAdd([]);
		event.preventDefault();
		const pastedText = event.clipboardData.getData('text');
		if (pastedText.trim() === '') {
			setNothingToPasteError(true);
		} else {
			const newRows = pastedText.trim().split('\n');
			const formattedRows: KeyphraseRow[] = newRows.map((row) => ({ ...EMPTY_ROW, terms: row }));
			setRowsToAdd(formattedRows);
			if (formattedRows.length > 499 || newRows.some((row) => row.length > 50)) {
				if (formattedRows.length > 499) {
					setExcessiveLines(newRows.length);
				}
				if (newRows.some((row) => row.length > 50)) {
					setExcessiveLength(true);
				}
			} else {
				onAdd(formattedRows);
			}
		}
	};

	const handleConfirmPaste = (lines: boolean) => {
		if (lines) {
			if (!excessiveLength) {
				onAdd(rowsToAdd);
			}
			setExcessiveLines(null);
		} else {
			onAdd(rowsToAdd);
			setExcessiveLength(false);
		}
	};

	const handleCancelPaste = () => {
		setExcessiveLines(null);
		setExcessiveLength(false);
		setRowsToAdd([]);
	};

	const handleUndoPaste = () => {
		onDelete(rowsToAdd);
		setRowsToAdd([]);
	};

	const createCell = (
		cellIdx: number,
		fieldName: keyof KeyphraseRow,
		content: React.ReactElement,
		align: 'left' | 'right' | 'center' = 'left',
		tooltipPlacement: 'bottom' | 'left' | 'right' | 'top' = 'bottom'
	): EnhancedTableCell => {
		return {
			content: (
				<MultilineTooltip
					title={row.errorFields && row.errorFields[fieldName]?.length > 0 ? row.errorFields[fieldName] : []}
					placement={tooltipPlacement}
				>
					{content}
				</MultilineTooltip>
			),
			align,
			customSx: getErrorSx(fieldName),
			onKeyDown: (e) => {
				e.stopPropagation();

				if (e.key === 'Enter') {
					onAdd();
				}
				if (e.key === 'ArrowUp') {
					keyHandler(cellIdx, 'UP');
				}
				if (e.key === 'ArrowDown') {
					keyHandler(cellIdx, 'DOWN');
				}
				if (e.key === 'ArrowLeft') {
					keyHandler(cellIdx, 'LEFT');
				}
				if (e.key === 'ArrowRight') {
					keyHandler(cellIdx, 'RIGHT');
				}
			},
		};
	};

	const tableCells: EnhancedTableCell[] = [
		{
			content: (
				<Typography color={changeInProgress ? 'textDisabled' : 'textPrimary'} data-testid="keyphrase-table-row-number">
					{rowIdx + 1}
				</Typography>
			),
		},
		createCell(
			1,
			'pronoun',
			<TextField
				type="text"
				value={row.pronoun}
				fullWidth
				variant="standard"
				slotProps={{
					input: {
						disableUnderline: true,
					},
					htmlInput: {
						'aria-label': 'Enter Pronoun',
					},
				}}
				onChange={(e) => textChangeHandler(e, 'pronoun')}
				disabled={changeInProgress}
				data-testid="keyphrase-table-row-pronoun-entry"
				placeholder={row.maxWordAllowance > 0 ? '...' : undefined}
			/>
		),
		createCell(
			2,
			'maxWordAllowance',
			<TextField
				type="text"
				value={row.maxWordAllowance}
				inputMode="numeric"
				fullWidth
				variant="standard"
				// https://technology.blog.gov.uk/2020/02/24/why-the-gov-uk-design-system-team-changed-the-input-type-for-numbers/
				slotProps={{
					input: { disableUnderline: true },
					htmlInput: { pattern: '[0-9]*', maxLength: 3, 'aria-label': 'Enter Max Word Allowance' },
				}}
				onChange={(e) => textChangeHandler(e, 'maxWordAllowance')}
				disabled={changeInProgress}
				data-testid="keyphrase-table-row-max-word-allowance-entry"
			/>
		),
		createCell(
			3,
			'terms',
			<TextField
				type="text"
				value={row.terms}
				fullWidth
				variant="standard"
				slotProps={{ input: { disableUnderline: true }, htmlInput: { 'aria-label': 'Enter Terms' } }}
				onChange={(e) => textChangeHandler(e, 'terms')}
				disabled={changeInProgress}
				data-testid="keyphrase-table-row-terms-entry"
				placeholder="..."
				onPaste={pasteHandler}
			/>
		),
		createCell(
			4,
			'tag',
			<Autocomplete
				value={row.tag}
				onChange={(_, newVal) => {
					//replace any 'Add "X"' text the component might have added, also speech marks not allowed
					const trimmedVal = newVal.replace(/^Add "/, '').replaceAll('"', '');
					if (trimmedVal && !tagOptions.includes(trimmedVal)) {
						updateTagOptions([...tagOptions, trimmedVal]);
					}
					valChangeHandler(trimmedVal, 'tag');
				}}
				options={tagOptions}
				freeSolo
				renderInput={(params) => (
					<TextField
						{...params}
						variant="standard"
						slotProps={{
							input: { ...params.InputProps, disableUnderline: true },
							htmlInput: { ...params.inputProps, 'aria-label': 'Enter Tag' },
						}}
						data-testid="keyphrase-table-row-tag-entry"
						placeholder="..."
					/>
				)}
				filterOptions={(opt, params) => {
					const filtered = tagFilter(opt, params);
					const { inputValue } = params;

					if (inputValue && !tagOptions.includes(inputValue)) {
						filtered.push(`Add "${inputValue}"`);
					}
					return filtered;
				}}
				disableClearable
				fullWidth
				sx={{ width: '160px' }}
				disabled={changeInProgress}
			/>,
			'left',
			'right'
		),
		createCell(
			5,
			'matchWholeWords',
			<Checkbox
				name="keyphrase-table-row-match-whole-words-checkbox"
				checked={row.matchWholeWords || false}
				onChange={(_, checked) => valChangeHandler(checked, 'matchWholeWords')}
				disabled={changeInProgress}
				data-testid="keyphrase-table-row-match-whole-words-checkbox"
				slotProps={{
					input: { 'aria-label': 'Match Whole Words' },
				}}
			/>,
			'center'
		),
		{
			content: (
				<IconButton
					disabled={disableDelete || changeInProgress}
					onClick={() => onDelete()}
					data-testid="keyphrase-table-row-delete-button"
					aria-label={`Delete Row ${rowIdx + 1}`}
				>
					<Delete color={disableDelete || changeInProgress ? 'disabled' : 'error'} />
				</IconButton>
			),
			align: 'center',
		},
		{
			content: (
				<IconButton
					onClick={() => onAdd()}
					disabled={changeInProgress}
					data-testid="keyphrase-table-row-add-button"
					aria-label={`Add New Row`}
				>
					<Add />
				</IconButton>
			),
			align: 'center',
		},
	];

	return (
		<>
			<EnhancedTableRow cells={tableCells} rowName={`keyphrase-row-${rowIdx}`} rowRef={rowRef} footer={bottomRow} />
			<Popover
				open={nothingToPasteError}
				onClose={() => setNothingToPasteError(false)}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
			>
				<Alert severity="error" sx={{ width: '100%' }}>
					<Typography variant="h5">{`Couldn't find anything in the clipboard to paste`}</Typography>
				</Alert>
			</Popover>
			<Popover
				open={rowsToAdd.length > 0 && !excessiveLength && !excessiveLines}
				onClose={() => setRowsToAdd([])}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
			>
				<Alert
					severity="success"
					sx={{ width: '100%' }}
					data-testid="pasted-success-alert"
					action={
						<Button data-testid="undo-paste-button" onClick={handleUndoPaste} variant="outlined">
							Undo Paste
						</Button>
					}
				>
					<Typography variant="h5">{`Pasted ${rowsToAdd.length} terms`}</Typography>
				</Alert>
			</Popover>
			<AlertDialog
				title="Excessive number of terms pasted"
				message={[`It looks like you're trying to paste ${excessiveLines} terms. Do you want to insert them all?`]}
				isOpen={excessiveLines !== null}
				rightButtonAction={handleCancelPaste}
				rightButtonMessage="Cancel"
				leftButtonMessage="Yes"
				leftButtonAction={() => handleConfirmPaste(true)}
			/>
			<AlertDialog
				title="Excessive term length"
				message={[`At least one term pasted is over 50 characters in length. Paste terms anyway?`]}
				isOpen={excessiveLength && !excessiveLines}
				rightButtonAction={handleCancelPaste}
				rightButtonMessage="Cancel"
				leftButtonMessage="Yes"
				leftButtonAction={() => handleConfirmPaste(false)}
			/>
		</>
	);
};

export default KeyphraseTableRow;
